import React, {Component, ErrorInfo} from 'react';
import * as Sentry from '@sentry/react';
import {LoadingSpinner} from '../LoadingSpinner/LoadingSpinner';
import {isReloadingForStaleChunk} from '../../lib/preloadErrorRecovery';

interface Props {
  children?: React.ReactNode;
  fallback?: React.ReactNode | boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // YOJO-3428: the page is already reloading for a stale chunk; this error is not a real failure
    if (isReloadingForStaleChunk()) {
      return;
    }
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
      }
    });
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null
    });
  };

  render() {
    if (this.state.hasError || this.props.fallback === true) {
      // YOJO-3428: the page is already reloading for a stale chunk; show the spinner, not an error
      if (isReloadingForStaleChunk()) {
        return <LoadingSpinner />;
      }
      return (
        <div className="min-h-screen bg-white px-6 py-24 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-base font-semibold text-red-600">Error</p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Something went wrong
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <div className="mt-10">
              <button
                onClick={this.handleRetry}
                className="rounded-md bg-blue-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
